import { useEffect, useMemo, useRef, useState } from 'react';
import { usePromoUpload } from '@entities/adCampaign';
import type { PromoUploadStatusType } from '@entities/adCampaign';
import type { AdCampaignMediaTypeEnum, AdPromoPureType } from '@shared/api/graphql/generated';
import { getMutablePromoStatuses, toPromoUploadStatuses } from './promoStateUtils';

const usePromoState = (initialStatuses: PromoUploadStatusType[] = [], scopeKey?: string) => {
  const [statuses, setStatuses] = useState(() => getMutablePromoStatuses(initialStatuses));
  const current = useRef(statuses);
  const attempts = useRef(new Map<string, symbol>());
  const active = useRef(true);
  const scope = useRef(scopeKey);
  const previousScope = useRef(scopeKey);
  scope.current = scopeKey;
  const promoUpload = usePromoUpload();

  const apply = (next: PromoUploadStatusType[]) => {
    current.current = next;
    setStatuses(next);
  };

  const clear = () => {
    attempts.current.clear();
    apply([]);
  };

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
      attempts.current.clear();
    };
  }, []);

  useEffect(() => {
    if (previousScope.current !== scopeKey) {
      previousScope.current = scopeKey;
      clear();
    }
  }, [scopeKey]);

  const set = (adPromos: (AdPromoPureType & { promoAdId?: string })[]) => {
    attempts.current.clear();
    apply(toPromoUploadStatuses(adPromos, () => `local_${crypto.randomUUID()}`));
  };

  const remove = (id: string) => {
    attempts.current.delete(id);
    apply(current.current.filter((status) => status.id !== id));
  };

  const uploadPromo = (file: File, id: string, mediaType?: AdCampaignMediaTypeEnum) => {
    const attempt = Symbol(id);
    const uploadScope = scope.current;
    attempts.current.set(id, attempt);
    const isCurrent = () =>
      active.current &&
      scope.current === uploadScope &&
      attempts.current.get(id) === attempt &&
      current.current.some((item) => item.id === id);

    promoUpload
      .uploadPromo(file, mediaType)
      .then((result) => {
        if (!isCurrent()) return;
        const promo = result.data?.result?.promo;
        if (result.errors?.length || !promo?.id || result.data?.result?.ok === false) {
          throw result.errors?.[0] ?? new Error('Promo upload was not confirmed');
        }
        apply(
          current.current.map((status) =>
            status.id !== id
              ? status
              : {
                  ...status,
                  file: undefined,
                  promoId: promo.id,
                  promoAdId: undefined,
                  videoPromoId: undefined,
                  error: undefined,
                  filename: promo.filename,
                  isLoading: false,
                  type: promo.type,
                  duration: promo.duration,
                  sourcefiles: promo.sourcefiles,
                  previewUrl: promo.previewUrl,
                }
          )
        );
        attempts.current.delete(id);
      })
      .catch((error: unknown) => {
        if (!isCurrent()) return;
        apply(
          current.current.map((status) =>
            status.id !== id
              ? status
              : {
                  ...status,
                  error: error instanceof Error ? error : new Error(String(error)),
                  isLoading: false,
                }
          )
        );
        attempts.current.delete(id);
      });
  };

  const retry = (id: string, mediaType?: AdCampaignMediaTypeEnum) => {
    const status = current.current.find((item) => item.id === id);
    if (!status?.file || status.isLoading) return;
    apply(current.current.map((item) => (item.id === id ? { ...item, isLoading: true, error: undefined } : item)));
    uploadPromo(status.file, id, mediaType);
  };

  const upload = async (files: File[], mediaType?: AdCampaignMediaTypeEnum) => {
    if (!files.length) return;
    const additions: PromoUploadStatusType[] = files.map((file) => ({
      file,
      id: `local_${crypto.randomUUID()}`,
      title: file.name,
      filename: file.name,
      isLoading: true,
      sourcefiles: [],
    }));
    apply([...additions, ...current.current]);
    additions.forEach((status) => uploadPromo(status.file!, status.id!, mediaType));
  };

  const sortedStatuses = useMemo(
    () =>
      [...statuses].sort((a, b) => {
        if (a.error) return -1;
        if (b.error) return 1;
        return 0;
      }),
    [statuses]
  );

  const reorder = (next: PromoUploadStatusType[]) => apply(getMutablePromoStatuses(next));

  return {
    set,
    upload,
    remove,
    retry,
    clear,
    reorder,
    error: promoUpload.result.error,
    loading: promoUpload.result.loading,
    called: promoUpload.result.called,
    data: { statuses: sortedStatuses, rawStatuses: statuses },
  };
};

export default usePromoState;
